import { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { useServices } from '@/lib/ServicesContext';
import { tresciApi, type NowaPozycjaPaska } from '@/lib/api/tresci';
import { PozycjePaska, SprawdzWidokUzytkownika } from './PozycjePaska';
import { WyborOdbiorcow } from './WyborOdbiorcow';
import {
  DOPISEK_WYNIK_NIEZNANY, PUSCI_ODBIORCY, komunikatBledu, useDostepZapisuTresci, wynikNieznany, type WartoscOdbiorcow,
} from '@/lib/tresci/tresciUi';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { Karta } from '@/components/Karta';
import { useAuth } from '@/lib/auth/AuthContext';
import {
  MAKS_DLUGOSC_PASKA, dlugoscWedlugAplikacji, dlugoscWedlugSerwera, normalizujTresc, wygladaNaHtml,
} from '@/lib/tresci/pasekKomunikatow';
import { PodgladPaska } from './PodgladPaska';
import { KreatorOdbiorcow, type Zakres } from './KreatorOdbiorcow';
import { HarmonogramOdDo, type WartoscHarmonogramu } from './HarmonogramOdDo';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PASEK GŁÓWNY — EDYCJA, PODGLĄD I PUBLIKACJA (OWNER 2026-10-02: bezpieczna droga zapisu).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Zapis: panel → serwer Control Center (sesja + `app.content.*`) → system główny kluczem
 * maszynowym, którego przeglądarka nie zna. Szkic wymaga `write`, publikacja `publish`.
 * Pierwszeństwo (USER > SCHOOL > CITY > GLOBAL, priorytet, najnowsza publikacja) liczy system
 * główny — panel pokazuje jego wynik w „Sprawdź widok użytkownika".
 */

export function PasekPage() {
  const auth = useAuth();
  const mozeEdytowac = auth.has('app.content.write');
  const mozePublikowac = auth.has('app.content.publish');

  const [surowy, setSurowy] = useState('');
  const [jakoGosc, setJakoGosc] = useState(false);
  const [zakres, setZakres] = useState<Zakres>('GLOBAL');
  const [harmonogram, setHarmonogram] = useState<WartoscHarmonogramu | null>(null);
  const onHarmonogram = useCallback((w: WartoscHarmonogramu) => setHarmonogram(w), []);
  const [odbiorcy, setOdbiorcy] = useState<WartoscOdbiorcow>(PUSCI_ODBIORCY);
  const [priorytet, setPriorytet] = useState(0);
  const [wysylanie, setWysylanie] = useState(false);
  const [komunikat, setKomunikat] = useState<{ ok: boolean; tekst: string } | null>(null);
  const [odswiez, setOdswiez] = useState(0);
  const { api } = useServices();
  const zapis = useDostepZapisuTresci();

  const wynik = useMemo(() => (surowy ? normalizujTresc(surowy) : null), [surowy]);
  const tekst = wynik?.ok ? wynik.tekst : null;
  const naSerwerze = dlugoscWedlugSerwera(tekst ?? surowy);
  const wAplikacji = dlugoscWedlugAplikacji(tekst ?? surowy);
  const ponadLimit = naSerwerze > MAKS_DLUGOSC_PASKA;

  const brakOdbiorcow =
    (zakres === 'CITY' && odbiorcy.miasta.length === 0)
    || (zakres === 'SCHOOL' && odbiorcy.szkoly.length === 0)
    || (zakres === 'USER' && odbiorcy.uczniowie.length === 0);
  const gotowe = Boolean(tekst) && !ponadLimit && !brakOdbiorcow && (harmonogram?.ok ?? false);

  const wyslij = async (publikuj: boolean) => {
    if (!tekst) return;
    const pozycja: NowaPozycjaPaska = {
      message: tekst,
      scope: zakres,
      cityIds: zakres === 'CITY' ? odbiorcy.miasta.map((m) => m.id) : [],
      schoolIds: zakres === 'SCHOOL' ? odbiorcy.szkoly.map((x) => x.id) : [],
      userIds: zakres === 'USER' ? odbiorcy.uczniowie.map((u) => u.userId) : [],
      startsAt: harmonogram?.start ? harmonogram.start.toISOString() : null,
      endsAt: harmonogram?.koniec && !harmonogram.bezKonca ? harmonogram.koniec.toISOString() : null,
      priority: priorytet,
      publish: publikuj,
    };
    setWysylanie(true);
    try {
      const p = await tresciApi.utworzPasek(api, pozycja);
      setKomunikat({ ok: true, tekst: publikuj ? `Opublikowano pozycję #${p.id}.` : `Zapisano szkic #${p.id}.` });
      // Pusty formularz = przycisk znów nieaktywny: drugie kliknięcie nie utworzy drugiej pozycji.
      setSurowy('');
      setOdswiez((x) => x + 1);
    } catch (e) {
      // Wynik nieznany (sieć, czas, 5xx): pozycja MOGŁA powstać — lista czyta się ponownie, żeby operator
      // widział, czy jest, ZANIM kliknie drugi raz (inaczej duplikat). Odmowa (4xx): formularz bez zmian.
      const nieznany = wynikNieznany(e);
      setKomunikat({ ok: false, tekst: komunikatBledu(e) + (nieznany ? DOPISEK_WYNIK_NIEZNANY : '') });
      if (nieznany) setOdswiez((x) => x + 1);
    } finally {
      setWysylanie(false);
    }
  };

  return (
    <Box>
      <NaglowekEkranu
        tytul="Pasek główny"
        opis="Żółty pasek na ekranie głównym aplikacji ucznia. Komunikat zastępuje wyłącznie „Dowóz: …” — koszyk zostaje i cały pasek nadal prowadzi do koszyka."
      />

      <Stack spacing={3}>
        <Karta tytul="Treść">
          {!mozeEdytowac ? (
            <Alert severity="info" role="note" sx={{ mb: 2 }}>Tylko podgląd — nie masz uprawnień do edycji treści aplikacji.</Alert>
          ) : null}
          <TextField
            label="Treść komunikatu"
            value={surowy}
            onChange={(e) => setSurowy(e.target.value)}
            disabled={!mozeEdytowac}
            multiline
            minRows={2}
            fullWidth
            slotProps={{ htmlInput: { 'aria-describedby': 'licznik-paska' } }}
          />
          <Typography id="licznik-paska" variant="caption"
            sx={{ display: 'block', mt: 1, color: ponadLimit ? 'error.main' : 'text.secondary' }}>
            {naSerwerze} / {MAKS_DLUGOSC_PASKA} — tak liczy serwer (emoji to 2 znaki) · w aplikacji: {wAplikacji}
          </Typography>

          {wynik && !wynik.ok ? (
            <Alert severity="error" role="alert" sx={{ mt: 1 }}>{wynik.powod}</Alert>
          ) : null}
          {tekst && wygladaNaHtml(tekst) ? (
            <Alert severity="warning" role="status" sx={{ mt: 1 }}>
              Znaczniki HTML nie formatują tekstu — uczeń zobaczy je dosłownie, jako znaki.
            </Alert>
          ) : null}
        </Karta>

        <Karta tytul="Podgląd — tak zobaczy uczeń">
          <PodgladPaska tekst={tekst} gosc={jakoGosc} />
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox size="small" checked={jakoGosc} onChange={(e) => setJakoGosc(e.target.checked)} />}
            label="Jak niezalogowany gość"
          />
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            Koszyk i szkoła są przykładowe. Bez treści widać standardowy pasek.
          </Typography>
        </Karta>

        <Karta tytul="Kto zobaczy">
          <KreatorOdbiorcow zakres={zakres} onZakres={setZakres} wylaczone={!mozeEdytowac} />
          <WyborOdbiorcow zakres={zakres} wartosc={odbiorcy} onZmiana={setOdbiorcy} wylaczone={!mozeEdytowac} />
          <TextField
            sx={{ mt: 2, width: 200 }}
            type="number"
            label="Priorytet (0–100)"
            value={priorytet}
            disabled={!mozeEdytowac}
            onChange={(e) => setPriorytet(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
            helperText="W tym samym zakresie wyższy wygrywa; przy remisie — nowsza publikacja."
          />
        </Karta>

        <Karta tytul="Kiedy">
          <Alert severity="info" role="note" sx={{ mb: 2 }}>
            Początek jest wymagany (presety: „od teraz", „24 h", „do końca dnia"); bez końca — tylko po
            świadomym zaznaczeniu „bez daty końcowej", wtedy do ręcznego wyłączenia.
            Czas w panelu: Europe/Warsaw; do systemu głównego idzie UTC.
          </Alert>
          <HarmonogramOdDo onZmiana={onHarmonogram} wylaczone={!mozeEdytowac} etykietaGrupy="Harmonogram paska" />
        </Karta>

        {mozeEdytowac ? (
          <Box>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" disabled={!gotowe || wysylanie || !zapis.dostepny} onClick={() => wyslij(false)}>Zapisz szkic</Button>
              {mozePublikowac ? (
                <Button variant="contained" disabled={!gotowe || wysylanie || !zapis.dostepny} onClick={() => wyslij(true)}>Opublikuj</Button>
              ) : null}
            </Stack>
            {zapis.powod ? (
              <Alert severity="warning" role="status" data-testid="powod-braku-zapisu" sx={{ mt: 1 }}>{zapis.powod}</Alert>
            ) : null}
            {tekst && !(harmonogram?.ok ?? false) ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Ustaw okno w „Kiedy”: początek i koniec albo świadomie „bez daty końcowej”.
              </Typography>
            ) : null}
            {brakOdbiorcow ? (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                Wybierz co najmniej jednego odbiorcę w zakresie „{zakres}".
              </Typography>
            ) : null}
            {komunikat ? (
              <Alert severity={komunikat.ok ? 'success' : 'error'} role={komunikat.ok ? 'status' : 'alert'} data-testid="wynik-zapisu" sx={{ mt: 1 }}>
                {komunikat.tekst}
              </Alert>
            ) : null}
          </Box>
        ) : null}

        <Karta tytul="Pozycje paska">
          <PozycjePaska odswiez={odswiez} mozePublikowac={mozePublikowac} zapisDostepny={zapis.dostepny} />
        </Karta>

        <Karta tytul="Sprawdź widok użytkownika">
          <SprawdzWidokUzytkownika />
        </Karta>
      </Stack>
    </Box>
  );
}
